import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Snackbar,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@mui/material";
import {
    Add as AddIcon,
    CardGiftcard as CardGiftcardIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Inventory as InventoryIcon,
    QueryStats as QueryStatsIcon
} from "@mui/icons-material";
import {
    adjustShopStock,
    createShopProduct,
    deleteShopProduct,
    getAdminShopProducts,
    getShopOrders,
    getShopStatistics,
    updateShopProduct
} from "../api/shopApi";
import { getPlans } from "../api/planApi";
import { getAffiliateById } from "../api/affiliateApi";
import { formatCurrency } from "../utils/currency";

const emptyForm = {
    name: "",
    description: "",
    imageUrl: "",
    imageData: "",
    price: "",
    discountPrice: "",
    stockQuantity: 0,
    lowStockThreshold: 3
};

const emptyGiftCardForm = {
    name: "",
    description: "",
    imageUrl: "",
    imageData: "",
    redemptionMode: "amount",
    amount: "",
    selectedPlanId: "",
    validityDays: 365
};

const ShopManagement = ({ affiliateId }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [orders, setOrders] = useState([]);

    const [openProductDialog, setOpenProductDialog] = useState(false);
    const [editProduct, setEditProduct] = useState(null);
    const [productForm, setProductForm] = useState(emptyForm);

    const [openStockDialog, setOpenStockDialog] = useState(false);
    const [stockProduct, setStockProduct] = useState(null);
    const [stockDelta, setStockDelta] = useState("");
    const [stockReason, setStockReason] = useState("manual_adjustment");
    const [stockNote, setStockNote] = useState("");
    const [openGiftCardDialog, setOpenGiftCardDialog] = useState(false);
    const [giftCardForm, setGiftCardForm] = useState(emptyGiftCardForm);
    const [plans, setPlans] = useState([]);
    const [paymentCurrency, setPaymentCurrency] = useState("EUR");
    const formatMoney = (value, currency = paymentCurrency) => formatCurrency(value, currency || paymentCurrency);
    const getOrderCurrency = (order) => order?.currency || order?.paymentCurrency || paymentCurrency;
    const statisticsCurrency = statistics?.paymentCurrency || paymentCurrency;

    const [snackbar, setSnackbar] = useState({
        open: false,
        severity: "success",
        message: ""
    });

    const showToast = (severity, message) => {
        setSnackbar({ open: true, severity, message });
    };

    const lowStockCount = useMemo(
        () => products.filter((product) => !product.giftCardMeta && product.stockQuantity <= product.lowStockThreshold).length,
        [products]
    );

    const refreshProducts = async () => {
        const response = await getAdminShopProducts(affiliateId);
        setProducts(response.products || []);
    };

    const refreshStats = async () => {
        const response = await getShopStatistics(affiliateId);
        setStatistics(response);
    };

    const refreshOrders = async () => {
        const response = await getShopOrders(affiliateId);
        setOrders(response.orders || []);
    };

    const refreshAll = async () => {
        if (!affiliateId) return;
        setLoading(true);
        try {
            await Promise.all([refreshProducts(), refreshStats(), refreshOrders()]);
        } catch (error) {
            console.error("ShopManagement refresh error:", error);
            showToast("error", error?.response?.data?.message || "Failed to load shop data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void refreshAll();
    }, [affiliateId]);

    useEffect(() => {
        const loadAffiliateCurrency = async () => {
            if (!affiliateId) return;
            try {
                const response = await getAffiliateById(affiliateId);
                setPaymentCurrency(response?.paymentCurrency || "EUR");
            } catch (error) {
                console.error("ShopManagement load affiliate currency error:", error);
                setPaymentCurrency("EUR");
            }
        };
        void loadAffiliateCurrency();
    }, [affiliateId]);

    useEffect(() => {
        const loadPlans = async () => {
            if (!affiliateId) return;
            try {
                const data = await getPlans(affiliateId);
                const affiliatePlans = Array.isArray(data)
                    ? data.filter((plan) => Number(plan.affiliateId) === Number(affiliateId) && plan.active)
                    : [];
                setPlans(affiliatePlans);
            } catch (error) {
                console.error("ShopManagement load plans error:", error);
            }
        };
        void loadPlans();
    }, [affiliateId]);

    const handleOpenCreate = () => {
        setEditProduct(null);
        setProductForm(emptyForm);
        setOpenProductDialog(true);
    };

    const handleOpenEdit = (product) => {
        setEditProduct(product);
        setProductForm({
            name: product.name || "",
            description: product.description || "",
            imageUrl: product.imageUrl || "",
            imageData: product.imageData || "",
            price: product.price ?? "",
            discountPrice: product.discountPrice ?? "",
            stockQuantity: product.stockQuantity ?? 0,
            lowStockThreshold: product.lowStockThreshold ?? 3
        });
        setOpenProductDialog(true);
    };

    const handleImageFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            showToast("error", "Only image files are supported");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setProductForm((prev) => ({
                ...prev,
                imageData: String(reader.result || ""),
                imageUrl: ""
            }));
        };
        reader.readAsDataURL(file);
    };

    const handleSaveProduct = async () => {
        try {
            const payload = {
                affiliateId,
                name: productForm.name,
                description: productForm.description,
                imageUrl: productForm.imageData ? null : (productForm.imageUrl || null),
                imageData: productForm.imageData || null,
                price: Number(productForm.price),
                discountPrice: productForm.discountPrice === "" ? null : Number(productForm.discountPrice),
                stockQuantity: Number(productForm.stockQuantity),
                lowStockThreshold: Number(productForm.lowStockThreshold)
            };

            if (editProduct) {
                await updateShopProduct(editProduct.id, payload);
                showToast("success", "Product updated");
            } else {
                await createShopProduct(payload);
                showToast("success", "Product created");
            }

            setOpenProductDialog(false);
            setProductForm(emptyForm);
            await Promise.all([refreshProducts(), refreshStats()]);
        } catch (error) {
            console.error("handleSaveProduct error:", error);
            showToast("error", error?.response?.data?.message || "Failed to save product");
        }
    };

    const handleArchiveProduct = async (product) => {
        if (!window.confirm(`Archive product "${product.name}"?`)) return;
        try {
            await deleteShopProduct(product.id);
            showToast("success", "Product archived");
            await Promise.all([refreshProducts(), refreshStats()]);
        } catch (error) {
            console.error("handleArchiveProduct error:", error);
            showToast("error", error?.response?.data?.message || "Failed to archive product");
        }
    };

    const handleOpenStock = (product) => {
        setStockProduct(product);
        setStockDelta("");
        setStockReason("manual_adjustment");
        setStockNote("");
        setOpenStockDialog(true);
    };

    const handleSaveStock = async () => {
        if (!stockProduct) return;
        try {
            await adjustShopStock(stockProduct.id, {
                affiliateId,
                delta: Number(stockDelta),
                reason: stockReason,
                note: stockNote
            });
            showToast("success", "Stock updated");
            setOpenStockDialog(false);
            await Promise.all([refreshProducts(), refreshStats()]);
        } catch (error) {
            console.error("handleSaveStock error:", error);
            showToast("error", error?.response?.data?.message || "Failed to update stock");
        }
    };

    const handleGiftCardImageFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            showToast("error", "Only image files are supported");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setGiftCardForm((prev) => ({
                ...prev,
                imageData: String(reader.result || ""),
                imageUrl: ""
            }));
        };
        reader.readAsDataURL(file);
    };

    const handleOpenGiftCardCreate = () => {
        setGiftCardForm(emptyGiftCardForm);
        setOpenGiftCardDialog(true);
    };

    const handleSaveGiftCardProduct = async () => {
        try {
            const isPlanMode = giftCardForm.redemptionMode === "plan";
            const selectedPlan = plans.find((plan) => Number(plan.id) === Number(giftCardForm.selectedPlanId));
            const amount = isPlanMode ? Number(selectedPlan?.price || 0) : Number(giftCardForm.amount);
            if (!giftCardForm.name.trim()) {
                showToast("error", "Gift card name is required");
                return;
            }
            if (!Number.isFinite(amount) || amount <= 0) {
                showToast("error", "Gift card amount must be positive");
                return;
            }
            if (isPlanMode && !selectedPlan) {
                showToast("error", "Please select a redeemable plan");
                return;
            }

            const payload = {
                affiliateId,
                name: giftCardForm.name.trim(),
                description: giftCardForm.description.trim(),
                imageUrl: giftCardForm.imageData ? null : (giftCardForm.imageUrl || null),
                imageData: giftCardForm.imageData || null,
                price: amount,
                discountPrice: null,
                stockQuantity: 999999,
                lowStockThreshold: 0,
                giftCardMeta: {
                    type: "gift_card",
                    redemptionMode: giftCardForm.redemptionMode,
                    amount: !isPlanMode ? amount : undefined,
                    planId: isPlanMode ? Number(selectedPlan.id) : undefined,
                    validityDays: Number(giftCardForm.validityDays) || 365
                }
            };

            await createShopProduct(payload);
            setOpenGiftCardDialog(false);
            setGiftCardForm(emptyGiftCardForm);
            showToast("success", "Gift card product created");
            await Promise.all([refreshProducts(), refreshStats()]);
        } catch (error) {
            console.error("handleSaveGiftCardProduct error:", error);
            showToast("error", error?.response?.data?.message || "Failed to create gift card product");
        }
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
                <Typography variant="h5" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                    <InventoryIcon /> Shop Management
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button variant="contained" startIcon={<CardGiftcardIcon />} color="secondary" onClick={handleOpenGiftCardCreate}>
                        Add Gift Card
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                        Add Product
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                <Chip label={`Products: ${products.length}`} color="primary" />
                <Chip label={`Low stock: ${lowStockCount}`} color={lowStockCount > 0 ? "warning" : "default"} />
                <Chip label={`Orders: ${statistics?.totals?.orderCount || 0}`} color="secondary" />
            </Box>

            <Paper sx={{ mb: 2 }}>
                <Tabs value={activeTab} onChange={(event, next) => setActiveTab(next)}>
                    <Tab label="Products" />
                    <Tab label="Statistics" icon={<QueryStatsIcon />} iconPosition="start" />
                    <Tab label="Orders" />
                </Tabs>
            </Paper>

            {activeTab === 0 && (
                <Paper sx={{ overflowX: "auto" }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Discount</TableCell>
                                <TableCell>Final price</TableCell>
                                <TableCell>Stock</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {products.map((product) => {
                                const finalPrice = product.discountPrice ?? product.price;
                                const lowStock = product.stockQuantity <= product.lowStockThreshold;
                                return (
                                    <TableRow key={product.id}>
                                        <TableCell>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                {(product.imageData || product.imageUrl) ? (
                                                    <Box
                                                        component="img"
                                                        src={product.imageData || product.imageUrl}
                                                        alt={product.name}
                                                        sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }}
                                                    />
                                                ) : null}
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{product.name}</Typography>
                                                    {product.giftCardMeta ? (
                                                        <Chip size="small" color="secondary" label="Gift Card" sx={{ ml: 0.5 }} />
                                                    ) : null}
                                                    <Typography variant="caption" color="text.secondary">
                                                        {product.description || "No description"}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{formatMoney(product.price)}</TableCell>
                                        <TableCell>{product.discountPrice ? formatMoney(product.discountPrice) : "-"}</TableCell>
                                        <TableCell>{formatMoney(finalPrice)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={product.giftCardMeta ? "secondary" : (lowStock ? "warning" : "default")}
                                                label={product.giftCardMeta ? "Digital" : `${product.stockQuantity} pcs`}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" color={product.active ? "success" : "default"} label={product.active ? "Active" : "Archived"} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => handleOpenStock(product)} title="Adjust stock">
                                                <InventoryIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleOpenEdit(product)} title="Edit product">
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleArchiveProduct(product)} title="Archive product">
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            {activeTab === 1 && (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary">Total revenue</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{formatMoney(statistics?.totals?.totalRevenue || 0, statisticsCurrency)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary">Credit revenue</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{formatMoney(statistics?.totals?.totalCreditRevenue || 0, statisticsCurrency)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary">Montonio revenue</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{formatMoney(statistics?.totals?.totalMontonioRevenue || 0, statisticsCurrency)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper sx={{ overflowX: "auto" }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Product</TableCell>
                                        <TableCell>Sold units</TableCell>
                                        <TableCell>Revenue</TableCell>
                                        <TableCell>Avg unit price</TableCell>
                                        <TableCell>Current stock</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(statistics?.productStats || []).map((row) => (
                                        <TableRow key={row.productId}>
                                            <TableCell>{row.productName}</TableCell>
                                            <TableCell>{row.soldUnits}</TableCell>
                                            <TableCell>{formatMoney(row.revenue, statisticsCurrency)}</TableCell>
                                            <TableCell>{formatMoney(row.averageSalePrice, statisticsCurrency)}</TableCell>
                                            <TableCell>
                                                <Chip size="small" color={row.lowStock ? "warning" : "default"} label={row.stockQuantity} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {activeTab === 2 && (
                <Paper sx={{ overflowX: "auto" }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Order ref</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Credit</TableCell>
                                <TableCell>Montonio</TableCell>
                                <TableCell>Items</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((order) => (
                                <TableRow key={order.id}>
                                    <TableCell>{order.merchantReference}</TableCell>
                                    <TableCell>{new Date(order.createdAt).toLocaleString("et-EE")}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={order.paymentStatus} color={order.paymentStatus === "paid" ? "success" : "default"} />
                                    </TableCell>
                                    <TableCell>{formatMoney(order.totalAmount, getOrderCurrency(order))}</TableCell>
                                    <TableCell>{formatMoney(order.creditAmount, getOrderCurrency(order))}</TableCell>
                                    <TableCell>{formatMoney(order.montonioAmount, getOrderCurrency(order))}</TableCell>
                                    <TableCell>
                                        {(order.items || []).map((item) => `${item.productNameSnapshot} x${item.quantity}`).join(", ")}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            <Dialog open={openProductDialog} onClose={() => setOpenProductDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editProduct ? "Edit Product" : "Add Product"}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Product name"
                                fullWidth
                                value={productForm.name}
                                onChange={(event) => setProductForm((prev) => ({ ...prev, name: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                fullWidth
                                multiline
                                minRows={3}
                                value={productForm.description}
                                onChange={(event) => setProductForm((prev) => ({ ...prev, description: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Image URL (optional)"
                                fullWidth
                                value={productForm.imageUrl}
                                onChange={(event) => setProductForm((prev) => ({ ...prev, imageUrl: event.target.value, imageData: "" }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="outlined" component="label">
                                Upload image
                                <input hidden type="file" accept="image/*" onChange={handleImageFileChange} />
                            </Button>
                            {productForm.imageData ? (
                                <Typography variant="caption" sx={{ ml: 1 }}>
                                    Image file selected
                                </Typography>
                            ) : null}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label={`Price (${paymentCurrency})`}
                                type="number"
                                fullWidth
                                value={productForm.price}
                                onChange={(event) => setProductForm((prev) => ({ ...prev, price: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label={`Discount price (${paymentCurrency})`}
                                type="number"
                                fullWidth
                                value={productForm.discountPrice}
                                onChange={(event) => setProductForm((prev) => ({ ...prev, discountPrice: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Stock quantity"
                                type="number"
                                fullWidth
                                value={productForm.stockQuantity}
                                onChange={(event) => setProductForm((prev) => ({ ...prev, stockQuantity: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Low stock threshold"
                                type="number"
                                fullWidth
                                value={productForm.lowStockThreshold}
                                onChange={(event) => setProductForm((prev) => ({ ...prev, lowStockThreshold: event.target.value }))}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenProductDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveProduct}
                        disabled={!productForm.name || !productForm.price || Number(productForm.price) <= 0}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openStockDialog} onClose={() => setOpenStockDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Adjust Stock</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {stockProduct?.name}
                    </Typography>
                    <TextField
                        label="Stock delta (+/-)"
                        type="number"
                        fullWidth
                        value={stockDelta}
                        onChange={(event) => setStockDelta(event.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Reason"
                        fullWidth
                        value={stockReason}
                        onChange={(event) => setStockReason(event.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Note"
                        fullWidth
                        multiline
                        minRows={2}
                        value={stockNote}
                        onChange={(event) => setStockNote(event.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenStockDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveStock} disabled={!Number(stockDelta)}>
                        Update
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openGiftCardDialog} onClose={() => setOpenGiftCardDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create Gift Card Product</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Gift card name"
                                fullWidth
                                value={giftCardForm.name}
                                onChange={(event) => setGiftCardForm((prev) => ({ ...prev, name: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Description (optional)"
                                fullWidth
                                multiline
                                minRows={2}
                                value={giftCardForm.description}
                                onChange={(event) => setGiftCardForm((prev) => ({ ...prev, description: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Image URL (optional)"
                                fullWidth
                                value={giftCardForm.imageUrl}
                                onChange={(event) => setGiftCardForm((prev) => ({
                                    ...prev,
                                    imageUrl: event.target.value,
                                    imageData: ""
                                }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="outlined" component="label">
                                Upload image
                                <input hidden type="file" accept="image/*" onChange={handleGiftCardImageFileChange} />
                            </Button>
                            {giftCardForm.imageData ? (
                                <Typography variant="caption" sx={{ ml: 1 }}>
                                    Image file selected
                                </Typography>
                            ) : null}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel id="gift-card-mode-label">Gift card type</InputLabel>
                                <Select
                                    labelId="gift-card-mode-label"
                                    label="Gift card type"
                                    value={giftCardForm.redemptionMode}
                                    onChange={(event) => setGiftCardForm((prev) => ({
                                        ...prev,
                                        redemptionMode: event.target.value,
                                        selectedPlanId: event.target.value === "amount" ? "" : prev.selectedPlanId
                                    }))}
                                >
                                    <MenuItem value="amount">Amount</MenuItem>
                                    <MenuItem value="plan">Plan</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Validity (days)"
                                type="number"
                                fullWidth
                                value={giftCardForm.validityDays}
                                onChange={(event) => setGiftCardForm((prev) => ({ ...prev, validityDays: event.target.value }))}
                            />
                        </Grid>
                        {giftCardForm.redemptionMode === "amount" ? (
                            <Grid item xs={12}>
                                <TextField
                                    label={`Amount (${paymentCurrency})`}
                                    type="number"
                                    fullWidth
                                    value={giftCardForm.amount}
                                    onChange={(event) => setGiftCardForm((prev) => ({ ...prev, amount: event.target.value }))}
                                />
                            </Grid>
                        ) : (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="gift-card-plan-label">Redeemable plan</InputLabel>
                                    <Select
                                        labelId="gift-card-plan-label"
                                        label="Redeemable plan"
                                        value={giftCardForm.selectedPlanId}
                                        onChange={(event) => setGiftCardForm((prev) => ({ ...prev, selectedPlanId: event.target.value }))}
                                    >
                                        {plans.map((plan) => (
                                            <MenuItem key={plan.id} value={plan.id}>
                                                {plan.name} - {formatMoney(plan.price)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenGiftCardDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveGiftCardProduct}
                        disabled={!giftCardForm.name || Number(giftCardForm.validityDays) <= 0}
                    >
                        Save Gift Card
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {loading ? <Typography sx={{ mt: 2 }}>Loading shop data...</Typography> : null}
        </Box>
    );
};

export default ShopManagement;
